import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, ArrowLeft, Sparkles, LogOut } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';
import { PRICING_CONFIG, priceLabel, yearlySavingsPercent } from '@/lib/pricingConfig';
import { detectDefaultCurrency, setStoredCurrency } from '@/lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_META = [
  {
    id: 'taster',
    label: 'Taster',
    tagline: 'Try the platform',
    features: ['8 keywords tracked', '1 blog/month', '100 backlinks target', 'Legal Translations included']
  },
  {
    id: 'growth',
    label: 'Growth',
    tagline: 'Serious about SEO',
    featured: true,
    features: ['20 keywords tracked', '3 blogs/month', '200 backlinks target', 'AI Visibility tracking', 'Account manager', 'Chat with your team']
  },
  {
    id: 'authority',
    label: 'Authority',
    tagline: 'Lead the market',
    features: ['40 keywords tracked', '8 blogs/month', '400 backlinks target', 'Full AI Visibility suite', 'Priority support', 'Custom reporting']
  }
];

const CURRENCIES = [
  { code: 'gbp', label: 'GBP £' },
  { code: 'eur', label: 'EUR €' },
  { code: 'usd', label: 'USD $' }
];

export default function PlanSelection({ onLogout }) {
  const navigate = useNavigate();
  const [billing, setBilling] = useState('monthly');
  const [currency, setCurrencyState] = useState(() => detectDefaultCurrency());
  const setCurrency = (c) => { setCurrencyState(c); setStoredCurrency(c); };
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    fetch(`${API}/stripe/payment-links`).then(r => r.json()).then(setLinks).catch(() => setLinks({}));
  }, []);

  const handleSelect = (planId) => {
    setLoading(planId);
    const key = `${planId}_${billing}_${currency}`;
    const url = links?.[key];
    if (url && !url.toLowerCase().includes('paste payment link')) {
      window.location.href = url;
    } else {
      alert('Payment link not available. Please try again or contact support.');
      setLoading(null);
    }
  };

  // Use Growth as the representative for the "save X%" label (all 3 plans share roughly the same yearly discount).
  const savingsPct = yearlySavingsPercent('growth', currency);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 px-4 py-10" data-testid="plan-selection-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <BrandLogo size={40} />
            <div className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Law Agency 360</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/dashboard/overview')}
              className="flex items-center gap-2 text-sm text-slate-300 hover:text-white px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition"
              data-testid="plan-selection-back"
            >
              <ArrowLeft className="w-4 h-4" /> Back to dashboard
            </button>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-white px-3 py-2 rounded-lg hover:bg-white/5 transition"
              data-testid="plan-selection-logout"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>

        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/15 border border-violet-500/25 text-violet-300 text-xs font-semibold uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Welcome to Law Agency 360
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit' }}>
            Choose a plan to get started
          </h1>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Select the plan that fits your legal firm. Your dashboard will be activated automatically after payment.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
          <div className="inline-flex p-1 bg-white/5 border border-white/10 rounded-full" data-testid="billing-toggle">
            {['monthly', 'yearly'].map(b => (
              <button
                key={b}
                onClick={() => setBilling(b)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition ${
                  billing === b ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white' : 'text-slate-300 hover:text-white'
                }`}
                data-testid={`billing-${b}`}
              >
                {b.charAt(0).toUpperCase() + b.slice(1)}
                {b === 'yearly' && savingsPct > 0 && (
                  <span className="ml-1 text-[10px] text-emerald-300">save {savingsPct}%</span>
                )}
              </button>
            ))}
          </div>
          <div className="inline-flex p-1 bg-white/5 border border-white/10 rounded-full" data-testid="currency-toggle">
            {CURRENCIES.map(c => (
              <button
                key={c.code}
                onClick={() => setCurrency(c.code)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                  currency === c.code ? 'bg-white/15 text-white' : 'text-slate-400 hover:text-white'
                }`}
                data-testid={`currency-${c.code}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {PLAN_META.map(plan => {
            const price = priceLabel(plan.id, billing, currency);
            // Sanity guard - surfaces a configuration drift early
            const has = !!PRICING_CONFIG[plan.id]?.[billing]?.[currency];
            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-2xl p-6 border transition-all ${
                  plan.featured
                    ? 'bg-gradient-to-b from-violet-900/30 to-indigo-900/20 border-violet-500/40 shadow-xl shadow-violet-900/30'
                    : 'bg-slate-800/40 border-white/5 hover:border-white/15'
                }`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{plan.label}</h3>
                <p className="text-sm text-slate-400 mb-4">{plan.tagline}</p>
                <div className="text-3xl font-bold text-white mb-1" data-testid={`plan-price-${plan.id}`}>{has ? price : '-'}</div>
                {billing === 'yearly' && has && (
                  <p className="text-xs text-emerald-300 mb-4">save {yearlySavingsPercent(plan.id, currency)}% vs monthly</p>
                )}
                {billing === 'monthly' && has && (
                  <p className="text-xs text-slate-500 mb-4">or {priceLabel(plan.id, 'yearly', currency)} (save {yearlySavingsPercent(plan.id, currency)}%)</p>
                )}
                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleSelect(plan.id)}
                  disabled={loading === plan.id}
                  className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                    plan.featured
                      ? 'bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:shadow-xl hover:shadow-violet-600/40'
                      : 'bg-white/5 text-white border border-white/10 hover:bg-white/10'
                  } disabled:opacity-60 disabled:cursor-not-allowed`}
                  data-testid={`plan-select-${plan.id}`}
                >
                  {loading === plan.id ? 'Redirecting…' : <>Get Started <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-slate-500 mt-10">
          Secured via Stripe. You'll be redirected to a hosted checkout page.
        </p>
      </div>
    </div>
  );
}
